import { createFileRoute, Link } from "@tanstack/react-router";
import {
  Bot,
  Zap,
  BarChart3,
  Smartphone,
  Plug,
  Clock,
  Timer,
  Hourglass,
  Gauge,
  Users,
  AlertTriangle,
  MessageCircle,
  Repeat,
  Phone,
  Eye,
  Flame,
  CheckCircle2,
  Crown,
  Brain,
  ListChecks,
  Sparkles,
  HeartHandshake,
  LineChart,
  ShieldCheck,
  Send,
  Award,
  ArrowRight,
  Calendar,
} from "lucide-react";
import { Breadcrumb, Card } from "./integracoes";
import { KENTRO } from "@/lib/constants";

const SITE = "https://kentro.com.br";
const PAGE_URL = `${SITE}/gestao-de-atendimento`;

const TITLE =
  "Gestão de Atendimento Digital com IA e WhatsApp | Kentro";
const DESC =
  "Organize seu atendimento no WhatsApp e em todos os canais com IA, CRM e KPIs em tempo real. +1.600 empresas e +14.670 agentes usam a Kentro. Agende uma demo.";

const FAQ: { q: string; a: string }[] = [
  {
    q: "Vou ter que trocar meu número de WhatsApp?",
    a: "Não. A migração para a API Oficial mantém o número que seus clientes já conhecem — sem perder histórico e sem comunicar troca.",
  },
  {
    q: "Quanto tempo leva para implementar?",
    a: "A maioria das operações entra no ar em poucos dias, com onboarding guiado pela Kentro Academy e suporte do time de implantação.",
  },
  {
    q: "Minha equipe vai conseguir usar?",
    a: "Sim. Treinamento por nível (agente, gerência e diretoria) está incluído na Kentro Academy, com trilhas práticas para cada papel.",
  },
  {
    q: "Integra com meu ERP ou sistema atual?",
    a: "Sim. A Kentro oferece REST API aberta, Webhooks e SDKs com documentação clara, além de conectores prontos para os principais ERPs, CRMs e gateways de pagamento do mercado.",
  },
  {
    q: "É seguro e está em conformidade com a LGPD?",
    a: "Sim. A Kentro opera com criptografia, controle de acesso por papel e está em conformidade com a LGPD. Mantemos um Encarregado de Proteção de Dados (DPO) ativo.",
  },
  {
    q: "A IA funciona mesmo?",
    a: "Agende uma demonstração e veja a IA resolvendo um caso real ao vivo, com a base de conhecimento da sua operação.",
  },
];

export const Route = createFileRoute("/gestao-de-atendimento")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:url", content: PAGE_URL },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: DESC },
      {
        name: "keywords",
        content:
          "gestão de atendimento, plataforma de atendimento digital, atendimento com IA, WhatsApp API oficial, CRM omnichannel, chatbot IA",
      },
    ],
    links: [{ rel: "canonical", href: PAGE_URL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Início", item: `${SITE}/` },
            {
              "@type": "ListItem",
              position: 2,
              name: "Gestão de Atendimento",
              item: PAGE_URL,
            },
          ],
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Product",
          name: "Kentro — Gestão de Atendimento",
          description: DESC,
          brand: { "@type": "Brand", name: "Kentro Sistemas" },
          category: "Customer service software",
          url: PAGE_URL,
          offers: {
            "@type": "Offer",
            priceCurrency: "BRL",
            availability: "https://schema.org/InStock",
            url: PAGE_URL,
          },
          aggregateRating: {
            "@type": "AggregateRating",
            ratingValue: "4.8",
            ratingCount: "1600",
            bestRating: "5",
          },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQ.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
    ],
  }),
  component: GestaoAtendimentoPage,
});

const DEMO_URL = "/contato";
const WA_URL = KENTRO.whatsappComercialUrl;

// === DOR ===
const PAINS = [
  {
    icon: Clock,
    text: 'Cliente manda "oi" às 9h, alguém responde 9h47 — e ele já comprou do concorrente.',
  },
  {
    icon: Repeat,
    text: "O cliente repete a história para 3 atendentes diferentes e desiste no meio do caminho.",
  },
  {
    icon: Flame,
    text: "Seu número pessoal foi banido bem na hora que as vendas cresciam — e levou o histórico junto.",
  },
  {
    icon: Eye,
    text: "Ninguém sabe de onde vêm os leads, quanto cada canal gera, nem por que o cliente sumiu.",
  },
  {
    icon: AlertTriangle,
    text: "A equipe vive apagando incêndio, respondendo as mesmas perguntas, sem tempo para vender.",
  },
];

// === 6 PILARES ===
const PILLARS = [
  {
    icon: Crown,
    title: "Dono da conversa",
    desc: "Quando o cliente volta, cai sempre no mesmo atendente.",
    result: "−70% de repetição, resolução 3× mais rápida.",
  },
  {
    icon: Brain,
    title: "Contexto vivo",
    desc: "O atendente abre a conversa e já vê o resumo automático do caso e o histórico completo.",
    result: "0 perguntas repetidas.",
  },
  {
    icon: Timer,
    title: "Ritmo & SLA",
    desc: 'Mensagens de retomada automáticas ("vou analisar e volto em até 12 minutos").',
    result: "Previsibilidade no lugar de silêncio.",
  },
  {
    icon: ListChecks,
    title: "Fila inteligente",
    desc: "Distribuição automática por habilidade, prioridade e disponibilidade.",
    result: "Nada cai no esquecimento.",
  },
  {
    icon: Sparkles,
    title: "Automação humana",
    desc: "A IA resolve o repetitivo; o humano assume o que importa.",
    result: "Sem parecer robô.",
  },
  {
    icon: LineChart,
    title: "Métricas que importam",
    desc: "Mede resolução, retomada e reabertura — não só tempo de resposta.",
    result: "Você vê o problema antes do cliente reclamar.",
  },
];

const KPIS = [
  {
    icon: Users,
    title: "Quantidade de atendimentos por período",
    desc: "Revela a eficiência do setor e a necessidade de equipe.",
  },
  {
    icon: Timer,
    title: "TMA — Tempo Médio de Atendimento",
    desc: "Quanto tempo o agente leva para resolver cada caso.",
  },
  {
    icon: Hourglass,
    title: "TME — Tempo Médio de Espera",
    desc: "Quanto o cliente espera para ser atendido.",
  },
  {
    icon: Gauge,
    title: "Tempo de primeira resposta",
    desc: "Corresponde à expectativa do primeiro contato.",
  },
  {
    icon: CheckCircle2,
    title: "Taxa de resolução e reabertura",
    desc: "O que realmente mede a qualidade do atendimento.",
  },
];

const RESULTS = [
  { value: "+1.600", label: "empresas confiam na Kentro" },
  { value: "+14.670", label: "agentes operando todos os dias" },
  { value: "+150%", label: "de crescimento ano a ano" },
  { value: "−52%", label: "de custo de atendimento (case ISP)" },
  { value: "−28%", label: "de inadimplência com cobrança automática" },
  { value: "Destaque", label: "InovAtiva Brasil 2023.2" },
];

function GestaoAtendimentoPage() {
  return (
    <>
      {/* HERO */}
      <section className="gradient-hero relative overflow-hidden pt-24 pb-16">
        <div className="circuit-pattern absolute inset-0 opacity-25" />
        <div className="relative mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
          <Breadcrumb
            items={[{ label: "Início", to: "/" }, { label: "Gestão de Atendimento" }]}
          />
          <h1 className="mt-4 text-4xl font-bold tracking-tight md:text-5xl lg:text-6xl">
            Gestão de atendimento que para de{" "}
            <span className="gradient-text">perder venda no WhatsApp</span>
          </h1>
          <p className="mt-5 max-w-2xl text-lg text-muted-foreground md:text-xl">
            A plataforma mais completa para organizar atendimento, vendas e marketing em um só
            lugar — com <strong className="text-foreground">IA que entende contexto</strong>, CRM
            integrado e <strong className="text-foreground">API Oficial do WhatsApp</strong>.
            Escale resultados sem aumentar a equipe.
          </p>
          <div className="mt-7 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
            <Link
              to="/contato"
              className="gradient-primary shadow-glow inline-flex items-center justify-center gap-2 rounded-md px-5 py-3 text-sm font-semibold text-white hover:opacity-95"
            >
              <Calendar className="size-4" /> Agendar demonstração gratuita
            </Link>
            <a
              href={WA_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 rounded-md border border-border bg-background/40 px-5 py-3 text-sm font-semibold hover:bg-foreground/5"
            >
              <MessageCircle className="size-4" /> Falar no WhatsApp
            </a>
          </div>

          {/* Faixa de prova social */}
          <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-3 text-sm text-muted-foreground">
            <span className="inline-flex items-center gap-2">
              <Users className="size-4 text-primary-glow" /> +1.600 empresas
            </span>
            <span className="hidden h-1 w-1 rounded-full bg-border sm:inline-block" />
            <span className="inline-flex items-center gap-2">
              <HeartHandshake className="size-4 text-primary-glow" /> +14.670 agentes
            </span>
            <span className="hidden h-1 w-1 rounded-full bg-border sm:inline-block" />
            <span className="inline-flex items-center gap-2">
              <Award className="size-4 text-primary-glow" /> Startup Destaque InovAtiva Brasil
            </span>
          </div>
        </div>
      </section>

      {/* DOR */}
      <Section
        eyebrow="A dor real"
        title="Seu atendimento não está lento. Está desorganizado — e isso custa caro."
      >
        <ul className="mt-6 grid gap-3 md:grid-cols-2">
          {PAINS.map((p) => (
            <li
              key={p.text}
              className="flex items-start gap-3 rounded-xl border border-border bg-card/40 p-4"
            >
              <span className="mt-0.5 inline-flex size-9 shrink-0 items-center justify-center rounded-lg bg-destructive/15 text-destructive">
                <p.icon className="size-4" />
              </span>
              <span className="text-sm text-muted-foreground">{p.text}</span>
            </li>
          ))}
        </ul>
        <p className="mt-6 max-w-3xl text-base text-foreground/90 md:text-lg">
          O problema não é esforço. <strong>É método.</strong> O WhatsApp é assíncrono — o cliente
          entra, sai e volta esperando que você lembre da história dele.{" "}
          <span className="gradient-text font-semibold">A Kentro organiza isso.</span>
        </p>
      </Section>

      {/* 6 PILARES */}
      <Section
        eyebrow="O método Kentro"
        title="Os 6 pilares de um atendimento que vira referência (e não reclamação)"
      >
        <div className="mt-8 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {PILLARS.map((p, i) => (
            <article
              key={p.title}
              className="group relative flex flex-col rounded-2xl border border-border bg-card/40 p-6 transition hover:border-primary-glow/40"
            >
              <div className="flex items-center gap-3">
                <span className="inline-flex size-10 items-center justify-center rounded-lg bg-primary/15 text-primary-glow">
                  <p.icon className="size-5" />
                </span>
                <span className="font-mono text-xs text-muted-foreground">
                  0{i + 1}
                </span>
              </div>
              <h3 className="mt-4 text-lg font-semibold">{p.title}</h3>
              <p className="mt-2 text-sm text-muted-foreground">{p.desc}</p>
              <p className="mt-4 text-sm font-semibold text-primary-glow">
                → {p.result}
              </p>
            </article>
          ))}
        </div>
      </Section>

      {/* CHATBOT IA */}
      <Section
        eyebrow="Chatbot com IA"
        title="Chatbot com IA que entende, consulta e resolve — não só responde"
        text="Esteja disponível em um instante, 24/7, com um chatbot criado sem código e treinado no seu negócio."
      >
        <div className="mt-6 grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <Card icon={<Zap className="size-5" />} title="Atendimento eficaz">
            Triagem instantânea, respostas certas e escalonamento para humanos quando faz sentido.
          </Card>
          <Card icon={<Bot className="size-5" />} title="IA generativa">
            Modelos com a base de conhecimento da sua empresa — resolvem até 80% das demandas
            comuns sozinhos.
          </Card>
          <Card icon={<Plug className="size-5" />} title="Sem código">
            Editor visual de fluxos e prompts — qualquer pessoa monta uma jornada.
          </Card>
          <Card icon={<Brain className="size-5" />} title="Análise de sentimento">
            Prioriza clientes críticos antes que virem reclamação.
          </Card>
        </div>
      </Section>

      {/* VENDAS / API OFICIAL */}
      <Section
        eyebrow="Vendas no WhatsApp"
        title="Velocidade nas vendas, com a segurança da API Oficial do WhatsApp"
        text="Amplie sua resposta com modelos integrados do WhatsApp e envios automáticos por gatilho. O diferencial é a personalização: use os dados do cliente para evitar a impressão de interação robótica."
      >
        <ul className="mt-6 grid gap-3 md:grid-cols-2">
          {[
            {
              icon: ShieldCheck,
              text: "Sem risco de banimento — estabilidade da API Oficial da Meta.",
            },
            {
              icon: Send,
              text: "Disparos em escala com segurança, sem queimar o número.",
            },
            {
              icon: Phone,
              text: "Migração sem trocar de número — você mantém o contato que o cliente já conhece.",
            },
            {
              icon: Users,
              text: "Múltiplos atendentes no mesmo número, com selo verificado.",
            },
          ].map((b) => (
            <li
              key={b.text}
              className="flex items-start gap-3 rounded-xl border border-border bg-card/40 p-4"
            >
              <span className="mt-0.5 inline-flex size-9 shrink-0 items-center justify-center rounded-lg bg-primary/15 text-primary-glow">
                <b.icon className="size-4" />
              </span>
              <span className="text-sm text-foreground/90">{b.text}</span>
            </li>
          ))}
        </ul>
      </Section>

      {/* KPIs */}
      <Section
        eyebrow="Indicadores"
        title="Decisões baseadas em dados, em tempo real"
        text="Supervisão, estatísticas e relatórios que mostram a eficiência do atendimento e facilitam a tomada de decisão."
      >
        <div className="mt-6 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {KPIS.map((k) => (
            <div key={k.title} className="rounded-xl border border-border bg-card/40 p-5">
              <div className="inline-flex size-10 items-center justify-center rounded-lg bg-primary/15 text-primary-glow">
                <k.icon className="size-5" />
              </div>
              <h3 className="mt-3 text-base font-semibold">{k.title}</h3>
              <p className="mt-1 text-sm text-muted-foreground">{k.desc}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* TUDO EM UM LUGAR */}
      <Section
        eyebrow="Plataforma única"
        title="Pare de pagar (e remendar) 5 softwares diferentes"
        text="Atendimento, CRM, automação, cobrança, telefonia e BI numa só plataforma — integrada e pronta para escalar."
      >
        <div className="mt-7 grid gap-4 md:grid-cols-2">
          <div className="rounded-2xl border border-destructive/30 bg-destructive/5 p-6">
            <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-destructive">
              <AlertTriangle className="size-4" /> Antes
            </div>
            <ul className="mt-4 space-y-3 text-sm text-muted-foreground">
              <li className="flex items-start gap-2"><span className="mt-1 size-1.5 shrink-0 rounded-full bg-destructive" /> Canais soltos, cada um em uma aba.</li>
              <li className="flex items-start gap-2"><span className="mt-1 size-1.5 shrink-0 rounded-full bg-destructive" /> Cliente repete a história em todo contato.</li>
              <li className="flex items-start gap-2"><span className="mt-1 size-1.5 shrink-0 rounded-full bg-destructive" /> Sem histórico unificado.</li>
              <li className="flex items-start gap-2"><span className="mt-1 size-1.5 shrink-0 rounded-full bg-destructive" /> Sem métricas confiáveis.</li>
              <li className="flex items-start gap-2"><span className="mt-1 size-1.5 shrink-0 rounded-full bg-destructive" /> Número instável, risco de banimento.</li>
            </ul>
          </div>
          <div className="shadow-glow rounded-2xl border border-primary-glow/40 bg-gradient-to-br from-primary/10 to-card/40 p-6">
            <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-primary-glow">
              <CheckCircle2 className="size-4" /> Com a Kentro
            </div>
            <ul className="mt-4 space-y-3 text-sm text-foreground/90">
              <li className="flex items-start gap-2"><CheckCircle2 className="mt-0.5 size-4 shrink-0 text-primary-glow" /> Canais unificados em uma só caixa.</li>
              <li className="flex items-start gap-2"><CheckCircle2 className="mt-0.5 size-4 shrink-0 text-primary-glow" /> Dono de conversa: sempre o mesmo atendente.</li>
              <li className="flex items-start gap-2"><CheckCircle2 className="mt-0.5 size-4 shrink-0 text-primary-glow" /> Histórico completo e contexto vivo.</li>
              <li className="flex items-start gap-2"><CheckCircle2 className="mt-0.5 size-4 shrink-0 text-primary-glow" /> KPIs em tempo real.</li>
              <li className="flex items-start gap-2"><CheckCircle2 className="mt-0.5 size-4 shrink-0 text-primary-glow" /> API Oficial do WhatsApp, estável.</li>
            </ul>
          </div>
        </div>
      </Section>

      {/* RESULTADOS */}
      <Section
        eyebrow="Resultados"
        title="Não prometemos. Comprovamos."
      >
        <div className="mt-7 grid gap-4 sm:grid-cols-2 md:grid-cols-3">
          {RESULTS.map((r) => (
            <div
              key={r.label}
              className="rounded-2xl border border-border bg-card/40 p-6 text-center"
            >
              <div className="gradient-text text-3xl font-bold md:text-4xl">{r.value}</div>
              <div className="mt-2 text-sm text-muted-foreground">{r.label}</div>
            </div>
          ))}
        </div>
      </Section>

      {/* DEPOIMENTOS placeholder */}
      <Section
        eyebrow="Depoimentos"
        title="Histórias de quem parou de perder cliente"
        text="Cases reais com nome, empresa, segmento e resultado mensurável. Em breve, novos vídeos."
      >
        <div className="mt-6 grid gap-4 md:grid-cols-3">
          {[
            { segment: "ISP", metric: "−52% custo de atendimento", body: "“Reduzimos meio time de suporte e o NPS subiu. A IA filtra, o humano resolve.”" },
            { segment: "Saúde", metric: "−38% no-show", body: "“Confirmação automática no WhatsApp encheu a agenda das clínicas.”" },
            { segment: "Varejo", metric: "+72% conversão WhatsApp", body: "“A Kentro virou nossa SDR principal. Dobramos vendas em 60 dias.”" },
          ].map((t) => (
            <div key={t.segment} className="rounded-2xl border border-border bg-card/40 p-6">
              <span className="inline-flex rounded-full border border-border bg-background/40 px-2.5 py-1 text-xs text-muted-foreground">
                {t.segment}
              </span>
              <p className="mt-4 text-base text-foreground/90">{t.body}</p>
              <p className="mt-4 text-sm font-semibold text-primary-glow">{t.metric}</p>
            </div>
          ))}
        </div>
        <p className="mt-6 text-sm text-muted-foreground">
          Quer ver um case completo do seu segmento?{" "}
          <Link to="/cases" className="text-primary-glow hover:underline">
            Acesse a página de cases
          </Link>
          .
        </p>
      </Section>

      {/* RECURSOS + APP */}
      <Section
        eyebrow="Recursos do dia a dia"
        title="Feito para o dia a dia de quem atende muito"
      >
        <div className="mt-6 grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <Card icon={<HeartHandshake className="size-5" />} title="Suporte rápido">
            O time da Kentro acompanha implantação e operação no dia a dia.
          </Card>
          <Card icon={<Smartphone className="size-5" />} title="Design responsivo">
            Opere do desktop ao celular sem perder recursos.
          </Card>
          <Card icon={<Gauge className="size-5" />} title="Alta performance">
            Plataforma otimizada para grande volume de mensagens.
          </Card>
          <Card icon={<Smartphone className="size-5" />} title="Apps Android e iOS">
            Atenda em movimento. Disponível nas lojas oficiais.
          </Card>
        </div>
        <div className="mt-6 flex flex-wrap gap-3">
          <a
            href="https://play.google.com/store/apps/details?id=com.kentro.kentro"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 rounded-md border border-border bg-background/40 px-4 py-2 text-sm font-semibold hover:bg-foreground/5"
          >
            <Smartphone className="size-4" /> Google Play
          </a>
          <a
            href="https://apps.apple.com/app/id1621140937"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 rounded-md border border-border bg-background/40 px-4 py-2 text-sm font-semibold hover:bg-foreground/5"
          >
            <Smartphone className="size-4" /> App Store
          </a>
        </div>
      </Section>

      {/* INTEGRAÇÕES */}
      <Section
        eyebrow="Integrações"
        title="Conecte tudo o que sua operação já usa"
        text="ERP, CRM, gateway de pagamento e +30 ferramentas via API REST e Webhooks."
      >
        <div className="mt-5">
          <Link
            to="/integracoes"
            className="gradient-primary shadow-glow inline-flex items-center gap-2 rounded-md px-5 py-3 text-sm font-semibold text-white hover:opacity-95"
          >
            <BarChart3 className="size-4" /> Ver todas as integrações
            <ArrowRight className="size-4" />
          </Link>
        </div>
      </Section>

      {/* FAQ */}
      <Section eyebrow="FAQ" title="Perguntas frequentes">
        <div className="mt-6 space-y-3">
          {FAQ.map((f) => (
            <details
              key={f.q}
              className="group rounded-xl border border-border bg-card/40 p-5 open:bg-card/60"
            >
              <summary className="flex cursor-pointer list-none items-start justify-between gap-4 font-semibold">
                <span>{f.q}</span>
                <span className="mt-1 text-muted-foreground transition group-open:rotate-45">+</span>
              </summary>
              <p className="mt-3 text-sm text-muted-foreground">{f.a}</p>
            </details>
          ))}
        </div>
        <p className="mt-6 text-sm text-muted-foreground">
          Outras dúvidas (planos, LGPD, integrações, segmentos):{" "}
          <Link to="/perguntas-frequentes" className="text-primary-glow hover:underline">
            ver todas as perguntas frequentes
          </Link>
          .
        </p>
      </Section>

      {/* CTA FINAL */}
      <section className="mx-auto my-12 max-w-5xl px-4 sm:px-6 lg:px-8">
        <div className="gradient-primary shadow-glow relative overflow-hidden rounded-3xl p-8 text-white md:p-12">
          <div className="relative">
            <h2 className="text-2xl font-bold tracking-tight md:text-4xl">
              Pronto para parar de perder venda no WhatsApp?
            </h2>
            <p className="mt-3 max-w-2xl text-base text-white/90 md:text-lg">
              Agende uma demonstração personalizada e veja a Kentro rodando com os fluxos da sua
              operação — em até 30 minutos.
            </p>
            <div className="mt-7 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
              <Link
                to={DEMO_URL}
                className="inline-flex items-center justify-center gap-2 rounded-md bg-white px-5 py-3 text-sm font-semibold text-[color:var(--primary)] hover:bg-white/90"
              >
                <Calendar className="size-4" /> Agendar demonstração gratuita
              </Link>
              <a
                href={WA_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 rounded-md border border-white/30 bg-white/10 px-5 py-3 text-sm font-semibold text-white hover:bg-white/20"
              >
                <MessageCircle className="size-4" /> Falar no WhatsApp
              </a>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

function Section({
  eyebrow,
  title,
  text,
  children,
}: {
  eyebrow?: string;
  title: string;
  text?: string;
  children?: React.ReactNode;
}) {
  return (
    <section className="mx-auto max-w-6xl px-4 py-14 sm:px-6 md:py-16 lg:px-8">
      {eyebrow ? (
        <div className="inline-flex items-center rounded-full border border-primary-glow/30 bg-primary/10 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-primary-glow">
          {eyebrow}
        </div>
      ) : null}
      <h2 className="mt-3 text-2xl font-bold tracking-tight md:text-4xl">{title}</h2>
      {text ? <p className="mt-3 max-w-3xl text-muted-foreground md:text-lg">{text}</p> : null}
      {children}
    </section>
  );
}
